import LogoutRounded from "@mui/icons-material/LogoutRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import UnfoldMoreRounded from "@mui/icons-material/UnfoldMoreRounded";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Outlet, useNavigate, useNavigation } from "react-router";

import { density } from "@/app/theme/tokens";
import { useLogout, useMe } from "@/features/auth";
import { Logo } from "@/shared/components/Logo";

import { ColorModeIconDropdown } from "./ColorModeIconDropdown";
import { CrumbProvider, Crumbs } from "./Crumbs";
import { MenuContent } from "./MenuContent";

const { sidebarWidth: SIDEBAR, topbarHeight: TOPBAR } = density;

/** 品牌条与顶栏同高，侧栏和内容区的第一行因此在同一条基线上。 */
function Brand() {
  return (
    <Stack
      direction="row"
      sx={{
        height: TOPBAR,
        flexShrink: 0,
        alignItems: "center",
        gap: 1,
        px: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "grid", placeItems: "center", color: "primary.main" }}>
        <Logo size={18} />
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 650, letterSpacing: -0.2 }} noWrap>
        Foxconn Center
      </Typography>
    </Stack>
  );
}

function UserCard() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const name = me.data?.username ?? "";
  return (
    <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider" }}>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="账号菜单"
        sx={{
          width: "100%",
          gap: 1,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          justifyContent: "flex-start",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main" }}>{name.slice(0, 1).toUpperCase()}</Avatar>
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, textAlign: "left", fontWeight: 500 }} noWrap>
          {name}
        </Typography>
        <UnfoldMoreRounded sx={{ fontSize: 16, color: "text.disabled" }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        slotProps={{ paper: { sx: { width: SIDEBAR - 16 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) });
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <LogoutRounded sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>退出登录</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <Stack sx={{ height: "100%" }}>
      <Brand />
      <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 1 }}>
        <MenuContent onNavigate={onNavigate} />
      </Box>
      <UserCard />
    </Stack>
  );
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigation = useNavigation();

  return (
    <CrumbProvider>
      <Box sx={{ display: "flex", height: "100dvh", overflow: "hidden", bgcolor: "background.default" }}>
        {navigation.state === "loading" && (
          <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, height: 2, zIndex: 2000 }} />
        )}

        <Box
          component="nav"
          sx={{
            display: { xs: "none", md: "block" },
            width: SIDEBAR,
            flexShrink: 0,
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <SidebarBody />
        </Box>
        <MuiDrawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { md: "none" }, [`& .${drawerClasses.paper}`]: { width: SIDEBAR } }}
        >
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </MuiDrawer>

        <Stack component="main" sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <Stack
            direction="row"
            sx={{
              height: TOPBAR,
              flexShrink: 0,
              alignItems: "center",
              gap: 1,
              px: { xs: 1.5, md: 2.5 },
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="打开导航"
              sx={{ display: { md: "none" }, ml: -0.5 }}
            >
              <MenuRounded sx={{ fontSize: 18 }} />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Crumbs />
            </Box>
            <ColorModeIconDropdown />
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {/* 列表页用 flex 撑满这块高度，所以这里给出确定的最小高度与列方向 */}
            <Box
              sx={{
                maxWidth: density.pageMaxWidth,
                mx: "auto",
                minHeight: "100%",
                px: { xs: 1.5, md: 2.5 },
                py: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Stack>
      </Box>
    </CrumbProvider>
  );
}
